import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { useTranslation, withTranslation } from 'react-i18next';
import { fontWeight } from 'styled-system';

import TagItem from 'Components/TagItem';
import Table from 'Components/Table';
import { Text, Box, Flex, Link, Icon, ICON_LIST } from 'Components/Base';
import { MODULE_STATUS, CONTENT_TYPE } from 'Domains/module';
import { isNil } from 'Util/utils';
import { SYLLABUS_MODE } from 'Domains/syllabus';
import Tooltip from 'Components/Tooltip';

const TextWrapper = styled(Box)`
  max-width: 275px;
  text-overflow: ellipsis;
  white-space: nowrap;
  overflow: hidden;
  text-align: center;
`;

const AttendanceTableStyle = styled(Table)`
  & .MuiTableCell-sizeSmall {
    padding: 5px 10px;
    border-right: 1px solid ${({ theme }) => theme.colors.white};
    border-bottom: none;

    &:nth-last-child(1) {
      border-right: none;
    }
  }

  & .MuiTableRow-head:nth-child(1) {
    & .MuiTableCell-head {
      &:nth-child(n + 4) {
        border-bottom: 1px solid ${({ theme }) => theme.colors.white};
      }
    }
  }

  & .MuiTableRow-head:nth-child(2) {
    & .MuiTableCell-head {
      &:nth-child(1) {
        width: 250px;
      }

      &:nth-child(n + 3) {
        min-width: 87px;
        width: 87px;
      }
    }
  }
`;

const TKAttendanceTableStyle = styled(Table)`
  & .MuiTableCell-sizeSmall {
    padding: 12px 16px;
    border-right: 1px solid ${({ theme }) => theme.colors.white};
    border-bottom: none;

    &:nth-last-child(1) {
      border-right: none;
    }
  }

  & .MuiTableRow-head:nth-child(1) {
    & .MuiTableCell-head {
      position: sticky;
      top: 0;
      z-index: 3;

      &:nth-child(n + 3) {
        border-bottom: 1px solid ${({ theme }) => theme.colors.white};
      }
    }
  }

  & .MuiTableRow-head:nth-child(2) {
    & .MuiTableCell-head {
      position: sticky;
      top: 49px !important;
      z-index: 3;

      &:nth-child(1) {
        width: 220px;
        min-width: 220px;
        * {
          justify-content: flex-start;
        }
      }
      &:nth-child(2) {
        width: 300px;
        min-width: 300px;
        * {
          justify-content: flex-start;
        }
      }
      &:nth-child(-n + 2) {
        > div {
          position: relative;
          top: -25px;
          display: flex;
          justify-content: center;
        }
      }
    }
  }
`;

const formatTextDisplay = text => {
  if (text && text.length > 30) {
    return `${text.slice(0, 30 - 4)}...${text.slice(-3)}`;
  }
  return text;
};

const TableHead = props => <Text fontWeight={600} {...props} />;
const HeaderColSpanText = styled(TableHead)`
  /* border-bottom: 1px solid ${({ theme }) => theme.colors.white}; */
  white-space: nowrap;
`;

// maxHeight is not styled system
export const AttendanceTable = ({ data, maxHeight, quizFilter, mode, ...props }) => {
  const { t } = useTranslation('summary');

  const getQuizArchetypesScoreAndTotal = (index, row) => {
    let score = row?.original?.quizArchetypes?.[index]?.firstScore;
    const total = row?.original?.quizArchetypes?.[index]?.total;

    if (quizFilter === 'max') {
      score = row?.original?.quizArchetypes?.[index]?.bestScore;
    } else if (quizFilter === 'avg') {
      score = row?.original?.quizArchetypes?.[index]?.avg;
    }

    return { score, total };
  };

  const columns = [
    {
      id: 'topic',
      Header: (
        <Box>
          <TableHead>{t('TOPIC')}</TableHead>
        </Box>
      ),
      accessor: 'sectionName',
      disableSortBy: true,
      Cell: ({ cell: { value } }) => (
        <Box>
          <Tooltip title={value}>
            <TextWrapper style={{ textAlign: 'left' }}>{value}</TextWrapper>
          </Tooltip>
        </Box>
      ),
    },
    {
      id: 'subsection',
      Header: <TableHead>{t('SUBSECTION')}</TableHead>,
      accessor: 'subsectionName',
      disableSortBy: true,
      Cell: ({ cell: { value } }) => (
        <Box>
          <Tooltip title={value}>
            <TextWrapper style={{ textAlign: 'left' }}>{formatTextDisplay(value)}</TextWrapper>
          </Tooltip>
        </Box>
      ),
    },
    {
      id: 'category',
      Header: <TableHead>{t('CATEGORY')}</TableHead>,
      accessor: 'contentType',
      disableSortBy: true,
      Cell: ({ cell: { value } }) => (
        <Box>
          <TextWrapper>{value === 'Video' ? CONTENT_TYPE.VDO : CONTENT_TYPE.QUIZ}</TextWrapper>
        </Box>
      ),
    },
    {
      id: 'attendance',
      Header: <HeaderColSpanText>{t('ATTENDANCE')}</HeaderColSpanText>,
      columns: [
        {
          id: 'numberStudent',
          Header: <TableHead>{t('NUMBER_OF_STUDENT')}</TableHead>,
          accessor: 'totalAttemptedStudents',
          disableSortBy: true,
          Cell: ({ cell: { value } }) => (
            <Box>
              <TextWrapper>{value}</TextWrapper>
            </Box>
          ),
        },
        {
          id: 'status',
          Header: <TableHead>{t('STATUS')}</TableHead>,
          accessor: 'status',
          disableSortBy: true,
          Cell: ({ cell: { value } }) => (
            <Flex justifyContent="center">
              <TagItem
                py="1"
                px="2"
                tagColor={value === MODULE_STATUS.COMPLETED ? 'primary' : 'inprogress'}
                justifyContent="center"
                width={'100px'}
              >
                <Text.Hint color="white">
                  {value === MODULE_STATUS.COMPLETED ? t('COMPLETED') : t('INCOMPLETED')}
                </Text.Hint>
              </TagItem>
            </Flex>
          ),
        },
      ],
    },
    {
      id: 'examResult',
      Header: <HeaderColSpanText>{t('EXAMINATION_RESULT')}</HeaderColSpanText>,
      columns: [
        {
          id: 'good',
          Header: (
            <TableHead>
              {t('GOOD')
                .split('\n')
                .reduce(
                  (init, next, index) => (init ? [...init, <br key={index} />, next] : [next]),
                  null,
                )}
            </TableHead>
          ),
          accessor: 'quizResult.good',
          disableSortBy: true,
          Cell: ({ cell: { value } }) => (
            <Box>
              <TextWrapper>{value}</TextWrapper>
            </Box>
          ),
        },
        {
          id: 'normal',
          Header: (
            <TableHead>
              {t('NORMAL')
                .split('\n')
                .reduce(
                  (init, next, index) => (init ? [...init, <br key={index} />, next] : [next]),
                  null,
                )}
            </TableHead>
          ),
          accessor: 'quizResult.normal',
          disableSortBy: true,
          Cell: ({ cell: { value } }) => (
            <Box>
              <TextWrapper>{value}</TextWrapper>
            </Box>
          ),
        },
        {
          id: 'bad',
          Header: (
            <TableHead>
              {t('BAD')
                .split('\n')
                .reduce(
                  (init, next, index) => (init ? [...init, <br key={index} />, next] : [next]),
                  null,
                )}
            </TableHead>
          ),
          accessor: 'quizResult.needImprovement',
          disableSortBy: true,
          Cell: ({ cell: { value } }) => (
            <Box>
              <TextWrapper>{value}</TextWrapper>
            </Box>
          ),
        },
      ],
    },
  ];

  const tkColumns = [
    {
      id: 'topic',
      Header: <TableHead>{t('TOPIC')}</TableHead>,
      accessor: 'sectionName',
      disableSortBy: true,
      Cell: ({ cell: { value } }) => (
        <Box>
          <Tooltip title={value}>
            <TextWrapper style={{ textAlign: 'left', fontWeight: 600 }}>{value}</TextWrapper>
          </Tooltip>
        </Box>
      ),
    },
    {
      id: 'sectionGroupName',
      Header: <TableHead>{t('MISSION')}</TableHead>,
      accessor: 'sectionGroupName',
      disableSortBy: true,
      Cell: ({ cell: { value } }) => (
        <Box>
          <Tooltip title={value}>
            <TextWrapper style={{ textAlign: 'left' }}>{formatTextDisplay(value)}</TextWrapper>
          </Tooltip>
        </Box>
      ),
    },
    {
      id: 'missionComplete',
      Header: <HeaderColSpanText>{t('MISSION_COMPLETE')}</HeaderColSpanText>,
      columns: [
        {
          id: 'videoCount',
          Header: <TableHead>{t('VIDEO')}</TableHead>,
          accessor: 'totalStudentCompletedVideo',
          disableSortBy: true,
          width: 80,
          Cell: ({ cell: { value }, row }) => (
            <Box>
              <TextWrapper>
                {row.original.isContainVideoModule ? (
                  <>
                    {value}/{row.original.totalStudents}
                  </>
                ) : (
                  '-'
                )}
              </TextWrapper>
            </Box>
          ),
        },
        {
          id: 'quizCount',
          Header: <TableHead>{t('QUIZ')}</TableHead>,
          accessor: 'totalStudentCompletedQuiz',
          disableSortBy: true,
          width: 80,
          Cell: ({ cell: { value }, row }) => (
            <Box>
              <TextWrapper>
                {row.original.isContainQuizModule ? (
                  <>
                    {value}/{row.original.totalStudents}
                  </>
                ) : (
                  '-'
                )}
              </TextWrapper>
            </Box>
          ),
        },
      ],
    },
    {
      id: 'quizArchetypes',
      Header: <HeaderColSpanText>{t('QUIZ_ARCHETYPES')}</HeaderColSpanText>,
      columns: [
        {
          id: 'a1',
          Header: <TableHead>{t('QUIZ_ARCHETYPES_A1')}</TableHead>,
          accessor: 'quizArchetypes',
          disableSortBy: true,
          Cell: ({ cell: { value }, row }) => {
            const { score, total } = getQuizArchetypesScoreAndTotal(0, row);
            return (
              <Box style={{ textAlign: 'center' }}>
                <TextWrapper>
                  {row.original.isContainQuizModule && total > 0 ? (
                    <>
                      {score}/{total}
                    </>
                  ) : (
                    ''
                  )}
                </TextWrapper>
              </Box>
            );
          },
        },
        {
          id: 'a3',
          Header: <TableHead>{t('QUIZ_ARCHETYPES_A3')}</TableHead>,
          accessor: 'quizArchetypes',
          disableSortBy: true,
          Cell: ({ cell: { value }, row }) => {
            const { score, total } = getQuizArchetypesScoreAndTotal(1, row);
            return (
              <Box style={{ textAlign: 'center' }}>
                {row.original.isContainQuizModule && total > 0 ? (
                  <>
                    {score}/{total}
                  </>
                ) : (
                  ''
                )}
              </Box>
            );
          },
        },
        {
          id: 'a5',
          Header: <TableHead>{t('QUIZ_ARCHETYPES_A5')}</TableHead>,
          accessor: 'quizArchetypes',
          disableSortBy: true,
          Cell: ({ cell: { value }, row }) => {
            const { score, total } = getQuizArchetypesScoreAndTotal(2, row);
            return (
              <Box style={{ textAlign: 'center' }}>
                {row.original.isContainQuizModule && total > 0 ? (
                  <>
                    {score}/{total}
                  </>
                ) : (
                  ''
                )}
              </Box>
            );
          },
        },
        {
          id: 'a9',
          Header: <TableHead>{t('QUIZ_ARCHETYPES_A9')}</TableHead>,
          accessor: 'quizArchetypes',
          disableSortBy: true,
          Cell: ({ cell: { value }, row }) => {
            const { score, total } = getQuizArchetypesScoreAndTotal(3, row);
            return (
              <Box style={{ textAlign: 'center' }}>
                {row.original.isContainQuizModule && total > 0 ? (
                  <>
                    {score}/{total}
                  </>
                ) : (
                  ''
                )}
              </Box>
            );
          },
        },
      ],
    },
  ];
  if (mode === SYLLABUS_MODE.TRADITIONAL) {
    return (
      <AttendanceTableStyle
        dense
        stickyHeader
        columns={columns}
        data={data}
        style={{ maxHeight }}
        {...props}
      />
    );
  } else {
    return (
      <TKAttendanceTableStyle
        dense
        stickyHeader
        columns={tkColumns}
        data={data}
        style={{ maxHeight }}
        {...props}
      />
    );
  }
};

AttendanceTable.propTypes = {
  data: PropTypes.array.isRequired,
  maxHeight: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  mode: PropTypes.string,
  quizFilter: PropTypes.string,
};

export default AttendanceTable;
